import React from "react";
import styles from "./BrazilPixReceipt.module.css";
import { BrazilPixReceiptData, BrazilPixReceiptParty } from "./types";

interface BrazilPixReceiptProps {
  data: BrazilPixReceiptData;
}

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const hasPartyContent = (party?: BrazilPixReceiptParty): boolean =>
  Boolean(party?.name || party?.document || party?.institution);

const PartyCard: React.FC<{
  label: string;
  party?: BrazilPixReceiptParty;
}> = ({ label, party }) => {
  if (!hasPartyContent(party)) return null;

  return (
    <div className={styles.party}>
      <div className={styles.partyLabel}>{label}</div>
      {party?.name && <div className={styles.partyName}>{party.name}</div>}
      {party?.document && (
        <div className={styles.partyDetail}>{party.document}</div>
      )}
      {party?.institution && (
        <div className={styles.partyDetail}>{party.institution}</div>
      )}
    </div>
  );
};

const BrazilPixReceipt: React.FC<BrazilPixReceiptProps> = ({ data }) => {
  const showPayer = hasPartyContent(data.payer);
  const showRecipient = hasPartyContent(data.recipient);
  const showParties = showPayer || showRecipient;

  const detailRows: { key: string; value: string; mono?: boolean }[] = [];
  if (data.endToEndId) {
    detailRows.push({
      key: "End-to-End ID",
      value: data.endToEndId,
      mono: true,
    });
  }
  if (data.transactionId) {
    detailRows.push({
      key: "Transaction ID",
      value: data.transactionId,
      mono: true,
    });
  }
  if (data.externalId) {
    detailRows.push({
      key: "External ID",
      value: data.externalId,
      mono: true,
    });
  }
  if (data.settledAt) {
    detailRows.push({ key: "Settled at", value: data.settledAt });
  }

  return (
    <main className={styles.receipt} id="brazil-pix-receipt">
      <div className={styles.receiptInner}>
        <header className={styles.header}>
          <div className={styles.brand}>
            <div>
              <div className={styles.brandTitle}>PIX transaction receipt</div>
            </div>
          </div>
          {data.status && <div className={styles.status}>{data.status}</div>}
        </header>

        {typeof data.amount === "number" && (
          <section className={styles.hero}>
            <div className={styles.eyebrow}>Amount</div>
            <div className={styles.amount}>{brl.format(data.amount)}</div>
          </section>
        )}

        {showParties && (
          <section className={styles.partyGrid}>
            <PartyCard label="Payer" party={data.payer} />
            {showPayer && showRecipient && (
              <div className={styles.flow}>→</div>
            )}
            <PartyCard label="Recipient" party={data.recipient} />
          </section>
        )}

        {detailRows.length > 0 && (
          <section className={styles.details}>
            {detailRows.map((row) => (
              <div className={styles.detailRow} key={row.key}>
                <div className={styles.detailKey}>{row.key}</div>
                <div
                  className={`${styles.detailValue}${
                    row.mono ? ` ${styles.mono}` : ""
                  }`}
                >
                  {row.value}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    </main>
  );
};

export default BrazilPixReceipt;
